import dayjs from '../../dayjs/dayjs_timezone.js';
import isoWeek from 'dayjs/plugin/isoWeek';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import objectSupport from 'dayjs/plugin/objectSupport';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import { findProp, prefixZero } from './helperUtility';
import {
  SHORT_DATE_FORMAT, DATE_TIME_FORMAT, FULL_DATE_TIME_FORMAT,
} from '../constants';

dayjs.extend(isoWeek);
dayjs.extend(customParseFormat)
dayjs.extend(objectSupport);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);

export const timePrefixZero = (time) => {
  const seconds = dayjs(time).unix() - dayjs().unix();
  const min = ~~(seconds / 60);
  const second = seconds % 60;
  if (seconds > 0) {
    return `${prefixZero(min, 2)}:${prefixZero(second, 2)}`;
  }
  return '00:00';
};

export const timePrefixZeroFromSeconds = (seconds) => {
  const min = ~~(seconds / 60);
  const second = seconds % 60;
  if (seconds > 0) {
    return `${prefixZero(min, 2)}:${prefixZero(second, 2)}`;
  }
  return '00:00';
};

export const secondsDiff = (time) => {
  const seconds = dayjs(time).unix() - dayjs().unix();
  return seconds > 0 ? seconds : 0;
};

export const timeFormat = (locale = window.bta.locale) => (locale.substr(0, 2) === 'en' ? 'h:mm A' : 'HH:mm');

export const cartTimeFormat = (locale = window.bta.locale) => (locale.substr(0, 2) === 'en' ? 'hh:mm A' : 'HH:mm');

/**
 * Finds a date and time property with the specified names.
 * @return the date as an ISO 8601 formatted string
 */

export const dateTimeFormat = (allDay = false, z = true) => {
  const { dateFormat } = window.bta;
  const format = dateFormat || 'll';

  if (allDay) {
    return format;
  }

  return `${format} ${timeFormat()} ${z ? 'z' : ''}`;
};

export const parseDate = (date) => {
  const { dateFormat } = window.bta;
  if (dateFormat.length === 8) {
    // like MM/DD/YY or DD/MM/YY
    return dayjs(date, `${dateFormat}YY`);
  }
  return dayjs(date, dateFormat);
};

/**
 * Parsing date from a date array.
 * @param {array} dateArray date array with format [year, month, day, hour, minute, second]
 */
export const parseDateFromArray = (dateArray) => {
  if (dateArray.length > 6) return 'WRONG FORMAT';
  return `${dateArray[0]}-${(`0${dateArray[1]}`).slice(-2)}-${(`0${dateArray[2]}`).slice(-2)}`;
};

export const parseSpecificAbbrTime = (time) => {
  let timeStr = time.trim().toLowerCase(),
      amAbbr = ['de', '午前', 'בבוקר'],
      pmAbbr = ['du', '午後', 'בערב'];

  if (amAbbr.some(x => timeStr.includes(x))) {
    amAbbr.map(abbr => timeStr = timeStr.replace(abbr, 'am'))
  }

  if (pmAbbr.some(x => timeStr.includes(x))) {
    pmAbbr.map(abbr => timeStr = timeStr.replace(abbr, 'pm'))
  }

  return timeStr;
};

/**
 * Convert a time string to iso format.
 */
export const parseTime = (time) => dayjs(parseSpecificAbbrTime(time), ['h:ma', 'h:m a', 'H:m']);

export const isValidDate = (str, format) => {
  const d = dayjs(str, format);
  if (d == null || !d.isValid()) return false;

  if (d.format('YYYY-MM-DD') === 'Invalid date') return false;

  return true;
};

export const isValidISOFormat = (str) => {
  let format = false;

  switch (str.length) {
    case 10:
      format = SHORT_DATE_FORMAT;
      break;
    case 16:
      format = DATE_TIME_FORMAT;
      break;
    case 19:
      format = FULL_DATE_TIME_FORMAT;
      break;
    default:
      break;
  }

  if (!format) return false;

  const d = dayjs(str, format);
  if (d == null || !d.isValid()) return false;
  if (d.format(format) !== str) return false;

  return true;
};

const getNames = (key) => {
  const dateTimeNames = JSON.parse(window.bta.dateTimeNames);
  return dateTimeNames[key];
};

export const formatStrartToEndClassDate = (start, finish, locale, timeZone, timeFormat, dateFormat, region) => {
  const startDate = new Date(start),
        finihshDate = new Date(finish),
        tf = timeFormat || 'h:mm a',
        df = dateFormat ? dateFormat : 'MMM D, YYYY',
        startTime = dayjs(startDate).tz(timeZone).locale(region).format(tf),
        finishTime = dayjs(finihshDate).tz(timeZone).locale(region).format(tf),
        finishDateSelected = dayjs(finihshDate).tz(timeZone).locale(region).format(df),
        dateSelected = dayjs(startDate).tz(timeZone).locale(region).format(df),
        sameDate = dateSelected == finishDateSelected;

  let firstStr = sameDate ? startTime + '-' + finishTime : startTime + ' ' + dateSelected + ' - ',
      secondStr = sameDate ? dateSelected : finishTime + ' ' + finishDateSelected;

  return [firstStr, secondStr]
};

export const findDateTime = (props, dateNames, timeNames, options = false) => {
  let opt1 = false;
  if (options && options.date && options.date.format) {
    opt1 = { type: 'date', format: options.date.format };
  }
  const dateStr = findProp(props, dateNames, opt1);

  // "2018-01-01T00:00"
  if (dateStr.length === 16) return dateStr;

  // 2019-06-24T09:30:00
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
  if (isoDateRegex.test(dateStr)) return dateStr;

  // "10/12/2018 12:00 AM"
  if (dateStr.length > 16) {
    return dayjs(dateStr, dateTimeFormat(false, false))
      .format()
      .substr(0, 16);
  }

  if (dateStr) {
    const date = parseDate(dateStr);
    let opt2 = false;
    if (options && options.time) {
      opt2 = { type: 'time' };
    }
    const timeStr = findProp(props, timeNames, opt2);
    if (timeStr) {
      const time = parseTime(timeStr.split('-')[0]);

      const datetime = date.set({
        hour: time.get('hour'),
        minute: time.get('minute'),
        second: time.get('second'),
      });

      return datetime.format().substr(0, 16);
    }

    return date.format('YYYY-MM-DD');
  }
  return false;
};

export const findStartDateTime = (props, options = false) => findDateTime(props, getNames('start_date_names'), getNames('start_time_names'), options);

export const findFinishDateTime = (props, options = false) => findDateTime(props, getNames('end_date_names'), getNames('end_time_names'), options);

export const formatHours = (x, locale, timeZone, format = '') => {
  return dayjs(x).tz(timeZone).format(timeFormatFromParams(locale, format));
};

export const timeFormatFromParams = (locale, format = '') => {
  let temp;
  switch (format) {
    case 'casual':
      temp = 'h:mm a';
      break;
    case '24_hours':
      temp = 'HH:mm';
      break;
    default:
      temp = timeFormat(locale);
  }

  return temp
}

export const formatPrice = (x, symbol) => [symbol, x.toFixed(2)].join('');

export const formatDate = (date, timeZone) => dayjs(date).tz(timeZone).format(dateTimeFormat(false, false));

const replaceFormatBasedLocale = (locale, format) => {
  switch (locale) {
    case 'ja':
      return format.replace('YYYY', 'YYYY年').replace('MMMM', 'M月').replace('DD', 'D日')
    default:
      return format;
  }
}

export const convertDateFormat = (locale) => {
  const { dateFormat } = window.bta;

  switch (dateFormat) {
    case 'MM/DD/YYYY':
    case 'MM.DD.YYYY':
      return replaceFormatBasedLocale(locale,'MMMM DD, YYYY');
    case 'DD/MM/YYYY':
    case 'DD.MM.YYYY':
      return replaceFormatBasedLocale(locale,'DD MMMM YYYY');
    case 'YYYY/MM/DD':
    case 'YYYY.MM.DD':
      return replaceFormatBasedLocale(locale,'YYYY MMMM DD');
    default:
      return replaceFormatBasedLocale(locale,'MMMM DD, YYYY');
  }
};

export const getTimeFrameFromDate = (date, timeZone, maxMonth) => {
  let start = dayjs.tz(date, timeZone);
  let finish;

  if (maxMonth) {
    let monthsCount = maxMonth < 12 ? maxMonth : 12;
    start = start.startOf('day');
    finish = start.startOf('day').add(monthsCount, 'months');
  } else {
    start = start.startOf('month').utc().startOf('week').startOf('day'); // - be the first day of the month
    finish = start.add(6, 'weeks'); // - should be in server timezone
  }

  return {
    start,
    finish,
  };
};

export const dateFromParams = (params, timeZone) => {
  if (!params) return null;
  const { start } = params;
  if (!start) return null;
  const date = start.includes('Z') ? dayjs(start).tz(timeZone) : dayjs.tz(start, timeZone);
  if (date.format() === 'Invalid date' || date.isBefore(dayjs())) return null;
  return date;
};

export const selectDateFromParams = (params, timeZone) => {
  if (!params) return null;
  const { select } = params;
  if (!select) return null;
  const date = select.includes('Z') ? dayjs(select).tz(timeZone) : dayjs.tz(select, timeZone);
  if (date.format() === 'Invalid date' || date.isBefore(dayjs())) return null;
  return date;
};
